/**
 * Message Types
 *
 * Types for messages and content blocks displayed in the UI.
 *
 * Block taxonomy types have been relocated to @vestra/agent-sdk.
 * Re-exported here so all existing consumers remain unchanged.
 */

import type {
  ContentBlock,
  TextBlock,
  ImageBlock,
  ThinkingBlock,
  ToolUseBlock,
  ToolResultBlock,
  QuestionBlock,
  TodoBlock,
  ErrorBlock,
  FileAttachmentBlock,
  CreditApprovalBlock,
  RoutineConfirmationBlock,
  ItemConfirmationBlock,
  TurnFooterBlock,
  CheckpointBlock,
  CompactionBlock,
  MemoryRecallBlock,
  NoticeBlock,
} from '@vestra/agent-sdk';

// Re-export the full block taxonomy from @vestra/agent-sdk
export type {
  ToolStatus,
  TodoStatus,
  TextBlock,
  ImageBlock,
  ThinkingBlock,
  ComposioToolCategory,
  FormattedResultItem,
  FormattedResult,
  ToolDisplay,
  ToolResultDisplay,
  ToolUseBlock,
  ToolResultBlock,
  QuestionOption,
  Question,
  QuestionBlock,
  Todo,
  TodoBlock,
  ErrorBlock,
  FileAttachmentBlock,
  MediaBlock,
  CreditApprovalBlock,
  RoutineConfirmationBlock,
  ItemConfirmationBlock,
  TurnFooterBlock,
  CheckpointBlock,
  CompactionBlock,
  MemoryRecallBlock,
  NoticeBlock,
  NoOutputBlock,
  ContentBlock,
  ContentBlockType,
} from '@vestra/agent-sdk';

/**
 * Message role - who sent the message
 */
export type MessageRole = 'user' | 'assistant';

// =============================================================================
// Message Identity
// =============================================================================

/**
 * Canonical identity for a message.
 *
 * Before this type existed, a single assistant message in the frontend was
 * keyed by one of three unrelated identifiers depending on where it came
 * from: an optimistic client id during streaming (`msg-<ts>-<rand>`),
 * the server-assigned DB UUID after reload, or the SDK's per-message UUID.
 * Every per-message event that crossed the network (MESSAGE_CREDIT_UPDATE,
 * MESSAGE_SYNC, credit updates) had to guess which one the local copy
 * carried, and failed silently when it guessed wrong.
 *
 * The discriminated union makes the transition explicit:
 *  - `optimistic` — message exists only in the browser. `clientId` is the
 *    only identity and it never leaves the client.
 *  - `persisted` — message has landed in the DB. `serverId` is the DB row
 *    id. `clientId` is preserved so React keys don't change mid-flight.
 *    `sdkMessageUuid` is the SDK's per-message uuid.
 *
 * The transition `optimistic → persisted` happens exactly once per message,
 * at the MESSAGE_COMPLETE / AGENT_END seam on the frontend. Once persisted
 * the identity is immutable.
 */
export type MessageIdentity =
  | {
      kind: 'optimistic';
      clientId: string;
      /** See `wireUuid` on the persisted variant — same client envelope uuid. */
      wireUuid?: string;
    }
  | {
      kind: 'persisted';
      /** Preserved to keep React keys stable across the reconciliation. */
      clientId?: string;
      /** DB row id (UUID). Primary identity for server-emitted events. */
      serverId: string;
      /** SDK per-message uuid. */
      sdkMessageUuid?: string;
      /**
       * Client-minted envelope uuid (`emitPayload.uuid`) — the join key the
       * staging lane (MESSAGE_STAGED / MESSAGE_PICKED_UP / STAGING_SYNC) keys
       * off. Stamped so a message that renders straight into the thread (an
       * idle-branch send) stays findable by its staging uuid: if the executor
       * then stages that same message mid-turn (turn-boundary race), the
       * staged handler can see the existing thread bubble and stay idempotent
       * against it instead of minting a duplicate chip. Distinct from
       * `serverId` (DB row) and `sdkMessageUuid` (SDK uuid); it is a
       * client-only identifier and is never sent back to the server as a
       * message id, so it does not participate in MESSAGE_SYNC reconciliation.
       */
      wireUuid?: string;
    };

/**
 * Canonical id for a message. Prefer this over reading `.id` directly —
 * it returns the right identity regardless of whether the message is still
 * optimistic or has been reconciled to a persisted DB row.
 */
export function getMessageCanonicalId(identity: MessageIdentity): string {
  return identity.kind === 'persisted' ? identity.serverId : identity.clientId;
}

/**
 * True if the given id string matches ANY of the identifiers this message
 * carries — client, server, or sdk uuid. Callers handling a per-message
 * event don't know which flavor of id the server / SDK just handed them,
 * so they search all three in one pass.
 */
export function matchesAnyMessageId(identity: MessageIdentity, id: string): boolean {
  if (identity.kind === 'optimistic') {
    return identity.clientId === id || identity.wireUuid === id;
  }
  return (
    identity.serverId === id ||
    identity.clientId === id ||
    identity.sdkMessageUuid === id ||
    identity.wireUuid === id
  );
}

// =============================================================================
// Messages
// =============================================================================

/**
 * A message in the conversation.
 *
 * `id` remains on the interface so legacy callers (React keys, etc.)
 * continue to work. New code should go through `identity` and the helpers
 * `getMessageCanonicalId` / `matchesAnyMessageId`, which correctly handle
 * both the optimistic and persisted states.
 */
export interface Message {
  id: string;
  role: MessageRole;
  content: ContentBlock[];
  timestamp: Date;
  /**
   * Canonical identity for this message. See `MessageIdentity` for the
   * reconciliation model.
   *
   * Optional here for backward compatibility with older serialized rows
   * and tests that predate the identity field. New code should always
   * populate it; readers should tolerate absence by treating it as a
   * persisted identity with `serverId = id`.
   */
  identity?: MessageIdentity;
  /**
   * SDK message UUID from the Claude Agent SDK — populated for messages
   * Nullable for older messages
   * and for rows from tasks that started before file checkpointing shipped.
   *
   * Consumers:
   * Mirrored on `identity.sdkMessageUuid` for persisted messages; kept as
   * a top-level field too so lookups work without an identity-shaped probe.
   */
  sdkMessageUuid?: string;
}

// =============================================================================
// Type Guards
// =============================================================================

export function isTextBlock(block: ContentBlock): block is TextBlock {
  return block.type === 'text';
}

export function isImageBlock(block: ContentBlock): block is ImageBlock {
  return block.type === 'image';
}

export function isThinkingBlock(block: ContentBlock): block is ThinkingBlock {
  return block.type === 'thinking';
}

export function isToolUseBlock(block: ContentBlock): block is ToolUseBlock {
  return block.type === 'tool_use';
}

export function isToolResultBlock(block: ContentBlock): block is ToolResultBlock {
  return block.type === 'tool_result';
}

export function isQuestionBlock(block: ContentBlock): block is QuestionBlock {
  return block.type === 'question';
}

export function isTodoBlock(block: ContentBlock): block is TodoBlock {
  return block.type === 'todo';
}

export function isErrorBlock(block: ContentBlock): block is ErrorBlock {
  return block.type === 'error';
}

export function isFileAttachmentBlock(block: ContentBlock): block is FileAttachmentBlock {
  return block.type === 'file_attachment';
}

export function isCreditApprovalBlock(block: ContentBlock): block is CreditApprovalBlock {
  return block.type === 'credit_approval';
}

export function isRoutineConfirmationBlock(block: ContentBlock): block is RoutineConfirmationBlock {
  return block.type === 'routine_confirmation';
}

export function isItemConfirmationBlock(block: ContentBlock): block is ItemConfirmationBlock {
  return block.type === 'item_confirmation';
}

export function isTurnFooterBlock(block: ContentBlock): block is TurnFooterBlock {
  return block.type === 'turn_footer';
}

export function isCheckpointBlock(block: ContentBlock): block is CheckpointBlock {
  return block.type === 'checkpoint';
}

export function isCompactionBlock(block: ContentBlock): block is CompactionBlock {
  return block.type === 'compaction';
}

export function isMemoryRecallBlock(block: ContentBlock): block is MemoryRecallBlock {
  return block.type === 'memory_recall';
}

export function isNoticeBlock(block: ContentBlock): block is NoticeBlock {
  return block.type === 'notice';
}
